import { relations } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core'
import { resource, systemDesignProblem } from './content.js'
import { studyPlanTypeEnum } from './enums.js'
import { problem, source } from './problems.js'

// =============================================================================
// STUDY PLANS (Admin Only)
// =============================================================================

export const studyPlan = pgTable('StudyPlan', {
  id: uuid('id').primaryKey().defaultRandom(),
  slug: text('slug').notNull().unique(),
  name: text('name').notNull(),
  description: text('description'),
  icon: text('icon'),
  url: text('url'),
  type: studyPlanTypeEnum('type').default('coding').notNull(),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

export const studyPlanSource = pgTable(
  'StudyPlanSource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    studyPlanId: uuid('studyPlanId')
      .notNull()
      .references(() => studyPlan.id, { onDelete: 'cascade' }),
    sourceId: uuid('sourceId')
      .notNull()
      .references(() => source.id, { onDelete: 'cascade' }),
    isPrimary: boolean('isPrimary').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (t) => [
    unique().on(t.studyPlanId, t.sourceId),
    index('StudyPlanSource_studyPlanId_idx').on(t.studyPlanId),
    index('StudyPlanSource_sourceId_idx').on(t.sourceId),
  ],
)

export const studyPlanProblem = pgTable(
  'StudyPlanProblem',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    studyPlanId: uuid('studyPlanId')
      .notNull()
      .references(() => studyPlan.id, { onDelete: 'cascade' }),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.studyPlanId, table.problemId),
    unique().on(table.studyPlanId, table.order),
    index('StudyPlanProblem_studyPlanId_idx').on(table.studyPlanId),
    index('StudyPlanProblem_problemId_idx').on(table.problemId),
  ],
)

export const studyPlanSystemDesign = pgTable(
  'StudyPlanSystemDesign',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    studyPlanId: uuid('studyPlanId')
      .notNull()
      .references(() => studyPlan.id, { onDelete: 'cascade' }),
    systemDesignProblemId: uuid('systemDesignProblemId')
      .notNull()
      .references(() => systemDesignProblem.id, { onDelete: 'cascade' }),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.studyPlanId, table.systemDesignProblemId),
    unique().on(table.studyPlanId, table.order),
    index('StudyPlanSystemDesign_studyPlanId_idx').on(table.studyPlanId),
    index('StudyPlanSystemDesign_systemDesignProblemId_idx').on(
      table.systemDesignProblemId,
    ),
  ],
)

/**
 * StudyPlanResource - Links resources to study plans
 */
export const studyPlanResource = pgTable(
  'StudyPlanResource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    studyPlanId: uuid('studyPlanId')
      .notNull()
      .references(() => studyPlan.id, { onDelete: 'cascade' }),
    resourceId: uuid('resourceId')
      .notNull()
      .references(() => resource.id, { onDelete: 'cascade' }),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('StudyPlanResource_studyPlan_resource_unique').on(
      table.studyPlanId,
      table.resourceId,
    ),
    index('StudyPlanResource_studyPlanId_idx').on(table.studyPlanId),
    index('StudyPlanResource_resourceId_idx').on(table.resourceId),
  ],
)

// =============================================================================
// RELATIONS
// =============================================================================

export const studyPlanRelations = relations(studyPlan, ({ many }) => ({
  sources: many(studyPlanSource),
  problems: many(studyPlanProblem),
  systemDesignProblems: many(studyPlanSystemDesign),
  resources: many(studyPlanResource),
}))

export const studyPlanSourceRelations = relations(
  studyPlanSource,
  ({ one }) => ({
    studyPlan: one(studyPlan, {
      fields: [studyPlanSource.studyPlanId],
      references: [studyPlan.id],
    }),
    source: one(source, {
      fields: [studyPlanSource.sourceId],
      references: [source.id],
    }),
  }),
)

export const studyPlanProblemRelations = relations(
  studyPlanProblem,
  ({ one }) => ({
    studyPlan: one(studyPlan, {
      fields: [studyPlanProblem.studyPlanId],
      references: [studyPlan.id],
    }),
    problem: one(problem, {
      fields: [studyPlanProblem.problemId],
      references: [problem.id],
    }),
  }),
)

export const studyPlanSystemDesignRelations = relations(
  studyPlanSystemDesign,
  ({ one }) => ({
    studyPlan: one(studyPlan, {
      fields: [studyPlanSystemDesign.studyPlanId],
      references: [studyPlan.id],
    }),
    systemDesignProblem: one(systemDesignProblem, {
      fields: [studyPlanSystemDesign.systemDesignProblemId],
      references: [systemDesignProblem.id],
    }),
  }),
)

export const studyPlanResourceRelations = relations(
  studyPlanResource,
  ({ one }) => ({
    studyPlan: one(studyPlan, {
      fields: [studyPlanResource.studyPlanId],
      references: [studyPlan.id],
    }),
    resource: one(resource, {
      fields: [studyPlanResource.resourceId],
      references: [resource.id],
    }),
  }),
)

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type StudyPlan = typeof studyPlan.$inferSelect
export type NewStudyPlan = typeof studyPlan.$inferInsert
export type StudyPlanSource = typeof studyPlanSource.$inferSelect
export type NewStudyPlanSource = typeof studyPlanSource.$inferInsert
export type StudyPlanProblem = typeof studyPlanProblem.$inferSelect
export type NewStudyPlanProblem = typeof studyPlanProblem.$inferInsert
export type StudyPlanSystemDesign = typeof studyPlanSystemDesign.$inferSelect
export type NewStudyPlanSystemDesign = typeof studyPlanSystemDesign.$inferInsert
export type StudyPlanResource = typeof studyPlanResource.$inferSelect
export type NewStudyPlanResource = typeof studyPlanResource.$inferInsert
